import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { WorkOrderListResponse } from '../api/types.js';
import { Card, EmptyState, PageHeader, PriorityBadge, Spinner } from '../components/ui.js';
import { formatCurrency, formatDate } from '../lib/format.js';

export function ApprovalQueuePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', 'awaiting-approval'],
    queryFn: () =>
      apiRequest<WorkOrderListResponse>('/api/work-orders', {
        query: { awaitingMyApproval: 'true', limit: 100 },
      }),
  });

  if (isLoading) return <Spinner label="Loading proposals awaiting approval" />;

  return (
    <>
      <PageHeader
        title="Awaiting your approval"
        subtitle="Proposals VGS has submitted for a USPS decision."
      />

      {(data?.items.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            title="Nothing is waiting on you"
            description="Proposals appear here as soon as VGS submits them."
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {data?.items.map((item) => (
            <li key={item.id}>
              <Link
                to={`/work-orders/${item.id}`}
                className="block rounded-lg bg-white p-4 shadow-sm ring-1 ring-amber-300 transition hover:shadow"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-brand-800">
                        {item.workOrderNumber}
                      </span>
                      <PriorityBadge priority={item.priority} />
                    </div>
                    <p className="mt-1 text-sm font-medium text-slate-900">{item.title}</p>
                    <p className="text-xs text-slate-600">
                      {item.facilityCode} · {item.facilityName} · submitted{' '}
                      {formatDate(item.submittedAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold tabular-nums text-slate-900">
                      {item.pendingProposalTotalCents !== null
                        ? formatCurrency(item.pendingProposalTotalCents)
                        : '—'}
                    </p>
                    <p className="text-xs text-slate-600">
                      {item.pendingApprovalAuthorityLabel
                        ? `${item.pendingApprovalAuthorityLabel} approval`
                        : 'Review and decide'}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
